/* =====================================================================================
   Studio's asset list, from the database.

   Studio rendered a compiled constant. With a backend that constant is empty by design, and
   nothing read studio.assets, so an asset created through the form vanished from the list the
   moment the page reloaded. studio.v_assets (migration 0088) returns each asset with its
   versions as JSON, each version carrying its claim-gate verdict (evaluated by the same
   function that refuses a submission), its review tasks and its annotations, in the shape the
   module already renders. This file maps rows to that shape and nothing more.
   ===================================================================================== */

import { BACKEND } from './db';
import { explain } from './dberrors';
import type { Asset, AssetVersion } from '../data/studio';

const live = () => BACKEND === 'supabase';

interface Row {
  id: string; job_code: string; title: string; asset_type: string; status: string;
  territories: string[] | null; audience: string[] | null; expires_on: string | null;
  owner: string | null; products: string[]; versions: any[];
}

function toAsset(r: Row): Asset {
  const versions: AssetVersion[] = (r.versions ?? []).map((v: any) => ({
    n: v.n, label: v.label, status: v.status, claimLevel: v.claimLevel,
    claims: v.claims ?? [], pages: v.pages ?? 1,
    submittedAt: v.submittedAt ?? undefined, decidedAt: v.decidedAt ?? undefined,
    locked: Boolean(v.locked), changeSummary: v.changeSummary ?? undefined,
    annotations: (v.annotations ?? []).map((a: any) => ({
      id: a.id, page: a.page, x: Number(a.x), y: Number(a.y), author: a.author,
      discipline: a.discipline, body: a.body, severity: a.severity, category: a.category ?? undefined,
      status: a.status, createdAt: a.createdAt, carriedForward: a.carriedForward || undefined,
    })),
    reviewers: (v.reviewers ?? []).map((w: any) => ({
      name: w.name, discipline: w.discipline, required: w.required !== false,
      decision: w.decision ?? undefined, decidedAt: w.decidedAt ?? undefined, note: w.note ?? undefined,
    })),
    gate: v.gate ?? [],
    dbId: v.id,
  }));
  return {
    id: r.id, jobCode: r.job_code, title: r.title, type: r.asset_type,
    products: r.products ?? [], territories: r.territories ?? [], audience: r.audience ?? [],
    owner: r.owner ?? '', status: r.status as Asset['status'],
    expiresOn: r.expires_on ?? undefined,
    /* A version-less asset (just created, proof not yet uploaded) still needs versions[0] to
       exist because the list reads it. A synthetic draft says exactly that. */
    versions: versions.length ? versions : [{
      n: 0, label: 'no proof yet', status: 'draft', claimLevel: 'corporate', claims: [],
      pages: 0, locked: false, annotations: [], reviewers: [], gate: [],
    }],
  };
}

export async function fetchAssets(): Promise<Asset[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('studio').from('v_assets').select('*').order('updated_at', { ascending: false });
  if (error) throw new Error(explain(error.message, 'read the Studio assets'));
  return ((data ?? []) as Row[]).map(toAsset);
}


/* A demonstration tenant's asset seed is written in the short shape of the commercial seed
   (title, assetType, audience, status, version, claimLevel, note) rather than the full Asset
   with versions and annotations. Studio never read it at all: the demonstration's Studio was
   empty while its tenant file listed six assets. This lifts that shape into the one the module
   renders. The claim vocabulary in that shape is a pharmaceutical one (promotional, scientific
   exchange, payer communication); mapped to the gate's levels, and a note that names the gate
   becomes a gate verdict so the list says "gate blocked" as the author intended. */
export function fromSimpleSeed(rows: unknown[], orgName: string): Asset[] {
  const LEVEL: Record<string, string> = {
    promotional: 'clinical_performance', scientific_exchange: 'technical',
    payer_communication: 'economic', corporate: 'corporate',
  };
  return (rows as any[]).filter((r) => r && r.title && !Array.isArray(r.versions)).map((r, i) => {
    const level = (LEVEL[r.claimLevel] ?? r.claimLevel ?? 'corporate');
    const blocked = /blocked at the claim gate/i.test(r.note ?? '');
    const status = blocked && r.status === 'in_review' ? 'changes_required' : (r.status ?? 'draft');
    const n = Number(r.version ?? 1);
    return {
      id: String(r.id ?? i), jobCode: r.jobCode ?? `${orgName.slice(0, 2).toUpperCase()}-${String(i + 1).padStart(4, '0')}`,
      title: r.title, type: r.assetType ?? 'other', products: [], territories: r.territories ?? ['USA'],
      audience: r.audience ? [String(r.audience)] : [], owner: r.owner ?? '', status,
      versions: [{
        n, label: `v${n}.0`, status: status === 'approved' ? 'approved' : status === 'in_review' ? 'in_review' : status === 'changes_required' ? 'changes_required' : 'draft',
        claimLevel: level as AssetVersion['claimLevel'], claims: [], pages: 1,
        locked: status === 'approved',
        annotations: r.note ? [{
          id: `${r.id ?? i}-note`, page: 1, x: 0.5, y: 0.3, author: 'Reviewer',
          discipline: 'regulatory', body: r.note, severity: blocked ? 'blocker' : 'comment',
          status: 'open', createdAt: '',
        }] : [],
        reviewers: [],
        gate: blocked ? [{ territory: (r.territories ?? ['USA'])[0], product: 'the product', status: 'investigational',
          maxAllowed: 'technical', message: r.note }] : [],
      }],
    } as Asset;
  });
}
